
import React, { useState } from 'react'
import { isOutputCountSettingKey } from '@genieartinc/schemas'
import { Icon } from '@/components/ui/icon'
import { cn } from '@/lib/utils'
import { Textarea } from '@/components/ui/textarea'
import { ParameterControl } from './components/ParameterControl'
import { OutpaintExpansionEditor } from './OutpaintExpansionEditor'
import type { NodeModelSettings } from '@genieartinc/schemas'
import type { MergedParameter } from '@/lib/ai/parameter-merge'
import { expansionSpec, isExpansionParameter } from '@/lib/canvas/outpaint-expansion'

interface NodeSettingsSystemPromptProps {
  systemPrompt: string
  onSystemPromptChange: (value: string) => void
}

/** System prompt section for text nodes — edit mode with save/cancel */
export const NodeSettingsSystemPrompt: React.FC<NodeSettingsSystemPromptProps> = ({
  systemPrompt,
  onSystemPromptChange,
}) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(systemPrompt)

  const handleEdit = () => {
    setDraft(systemPrompt)
    setEditing(true)
  }

  const handleSave = () => {
    onSystemPromptChange(draft)
    setEditing(false)
  }

  const handleCancel = () => {
    setDraft(systemPrompt)
    setEditing(false)
  }

  return (
    <div className="border-t border-border px-3 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="scroll-text" className="h-4 w-4 text-muted-foreground" />
          <span className="font-mono text-[11px] font-semibold uppercase tracking-wider">System Prompt</span>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={handleEdit}
            className="flex h-6 w-6 items-center justify-center rounded hover:bg-accent transition-colors"
            title="Edit system prompt"
          >
            <Icon name="pencil" className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        )}
      </div>

      {!editing && systemPrompt && (
        <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{systemPrompt}</p>
      )}

      {editing && (
        <div className="mt-3 space-y-2">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Enter system instructions for the AI model..."
            className="min-h-[80px] text-sm resize-y"
            rows={3}
            maxLength={4000}
            autoFocus
          />
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>{draft.length} / 4,000 characters</span>
            {draft.length > 3500 && (
              <span className="text-amber-500">Approaching limit</span>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** Built-in iterations stepper — controls how many child nodes are created */
const IterationsControl: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => {
  const clamp = (v: number) => Math.max(1, Math.min(10, v))
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon name="copy" className="h-4 w-4 text-muted-foreground" />
        <span className="font-mono text-[11px] font-semibold uppercase tracking-wider">Iterations</span>
      </div>
      <div className="flex items-center gap-0 rounded-md border border-border bg-background">
        <button
          type="button"
          onClick={() => onChange(clamp(value - 1))}
          disabled={value <= 1}
          className="flex h-8 w-8 items-center justify-center text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
        >
          –
        </button>
        <span className="flex h-8 w-10 items-center justify-center font-mono text-sm tabular-nums">
          {value}
        </span>
        <button
          type="button"
          onClick={() => onChange(clamp(value + 1))}
          disabled={value >= 10}
          className="flex h-8 w-8 items-center justify-center text-sm text-muted-foreground hover:text-foreground disabled:opacity-30 transition-colors"
        >
          +
        </button>
      </div>
    </div>
  )
}

interface NodeSettingsParameterSectionProps {
  settingsParams: MergedParameter[]
  localSettings: NodeModelSettings
  isModelChanging: boolean
  onSettingsChange: (key: string, value: unknown) => void
  pinnedParameters?: string[]
  onPinToggle?: (paramName: string) => void
  /** Connected source, so the expansion preview can show the original image. */
  sourceUrl?: string
  sourceSize?: { width: number; height: number } | null
}

/** Dynamic parameters section with loading state and collapsible toggle */
export const NodeSettingsParameterSection: React.FC<NodeSettingsParameterSectionProps> = ({
  settingsParams,
  localSettings,
  isModelChanging,
  onSettingsChange,
  pinnedParameters,
  onPinToggle,
  sourceUrl,
  sourceSize,
}) => {
  const [collapsed, setCollapsed] = useState(false)

  // Filter out every output-count key (num_images, num_outputs, n, ...) — the platform strips
  // them from job settings and the iterations control decides how many outputs are billed.
  const expansion = expansionSpec(settingsParams)
  const filteredParams = settingsParams.filter(p => !isOutputCountSettingKey(p.name) && !isExpansionParameter(p.name, expansion))

  if (isModelChanging) {
    return (
      <div className="border-t border-border px-3 py-4 space-y-4">
        <div className="h-20 bg-muted/50 rounded animate-pulse" />
        <div className="h-20 bg-muted/50 rounded animate-pulse" />
      </div>
    )
  }

  if (filteredParams.length === 0 && !expansion) {
    return null
  }

  return (
    <div className="border-t border-border px-3 py-4 flex flex-col min-h-0">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="group flex w-full items-center justify-between transition-colors hover:text-foreground mb-4"
      >
        <div className="flex items-center gap-2">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-wider">Node Settings</span>
          {pinnedParameters && pinnedParameters.length > 0 && (
            <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
              {pinnedParameters.length} pinned
            </span>
          )}
        </div>
        <Icon
          name="chevron-down"
          className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            collapsed && "rotate-180"
          )}
        />
      </button>

      {!collapsed && (
        <div className="flex-1 overflow-y-auto genie-scroll space-y-4 pr-1">
          {/* Iterations — how many child nodes to create */}
          <IterationsControl
            value={(localSettings.iterations as number) ?? 1}
            onChange={(v) => onSettingsChange('iterations', v)}
          />
          {pinnedParameters && pinnedParameters.length >= 6 && (
            <div className="flex items-center gap-1.5 rounded-md bg-amber-500/10 px-2.5 py-1.5 text-[11px] text-amber-600 dark:text-amber-400">
              <Icon name="triangle-alert" className="h-3.5 w-3.5 shrink-0" />
              <span>Many pinned parameters may reduce canvas readability</span>
            </div>
          )}
          {expansion && (
            <OutpaintExpansionEditor
              parameters={settingsParams}
              settings={localSettings}
              sourceUrl={sourceUrl}
              sourceSize={sourceSize}
              onPatch={(patch) => {
                for (const [key, value] of Object.entries(patch)) onSettingsChange(key, value)
              }}
            />
          )}
          {(() => {
            const basic = filteredParams.filter((param) => param.group === 'basic')
            const advanced = filteredParams.filter((param) => param.group === 'advanced')
            const rest = filteredParams.filter((param) => param.group !== 'basic' && param.group !== 'advanced')
            const renderParam = (param: MergedParameter) => (
              <ParameterControl
                key={param.name}
                parameter={param}
                value={localSettings[param.name]}
                onChange={(value) => onSettingsChange(param.name, value)}
                disabled={param.disabled}
              />
            )
            if (basic.length === 0 || advanced.length === 0) return filteredParams.map(renderParam)
            return (
              <>
                {basic.map(renderParam)}
                {rest.map(renderParam)}
                <details className="border-t border-border pt-3">
                  <summary className="cursor-pointer font-mono text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Advanced</summary>
                  <div className="mt-4 space-y-4">
                    {advanced.map(renderParam)}
                  </div>
                </details>
              </>
            )
          })()}
        </div>
      )}
    </div>
  )
}
